import React, { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth, JUST_AUTHENTICATED_FLAG, api } from '../context/AuthContext';

export default function Login() {
  const { toast } = useToast();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/login', {
        email: email.trim().toLowerCase(), password,
      });
      // Tell the SMS opt-in modal it may consider showing itself; consumed on next tick.
      sessionStorage.setItem(JUST_AUTHENTICATED_FLAG, '1');
      setSession(null, data.user);
      toast({ title: 'Signed in', description: `Welcome back, ${data.user.email}` });
      const target = location.state?.from || (data.user.role === 'admin' ? '/admin' : '/');
      navigate(target);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Login failed.';
      toast({ title: 'Login error', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
    // `api` is a module-level stable import — safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password, setSession, navigate, location.state, toast]);

  return (
    <div className="bg-brand-cream min-h-screen">
      <section className="bg-brand-green text-brand-cream py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Account</span>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">Sign in</h1>
          <p className="mt-4 text-brand-cream/75">Same account works on this site and in the mobile app.</p>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-md mx-auto px-5 sm:px-8">
          <form onSubmit={submit} className="bg-white rounded-2xl border border-brand-cream-dark p-8 md:p-10 space-y-5">
            <div>
              <label htmlFor="login-email" className="block text-sm text-brand-green/70 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
                <input id="login-email" type="email" autoComplete="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
                  placeholder="you@brokerage.com" />
              </div>
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm text-brand-green/70 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
                <input id="login-password" type={showPw ? 'text' : 'password'} autoComplete="current-password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-11 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
                  placeholder="Your password" />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-green/60 hover:text-brand-green">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-brand-green text-brand-cream font-semibold hover:scale-[1.01] transition-transform disabled:opacity-70">
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>) : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <div className="text-center">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-sm text-brand-green/70 hover:text-brand-green underline">
                Forgot your password?
              </Link>
            </div>
          </form>
          <p className="mt-6 text-center text-sm text-brand-green/70">
            New here?{' '}
            <Link to="/signup" className="underline text-brand-green font-semibold">Create an account</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
